import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import { useTranslation } from "react-i18next";

const CouponTable = ({
  coupons,
  selectedCoupons,
  onSelectCoupon,
  onSelectAll,
  onSort,
  sortConfig,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleStatus,
}) => {
  const { t } = useTranslation();
  const [hoveredRow, setHoveredRow] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: "bg-success text-success-foreground",
        label: t("active"),
      },
      inactive: {
        color: "bg-secondary text-secondary-foreground",
        label: t("inactive"),
      },
      expired: {
        color: "bg-error text-error-foreground",
        label: t("expired"),
      },
      scheduled: {
        color: "bg-warning text-warning-foreground",
        label: t("scheduled"),
      },
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const getDiscountDisplay = (coupon) => {
    switch (coupon?.type) {
      case "ratio":
        return `${coupon?.value}%`;
      case "fixed":
        return `$${coupon?.value}`;
      case "bogo":
        return "BOGO";
      case "shipping":
        return "Free Ship";
      default:
        return coupon?.value;
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const handleSort = (column) => {
    onSort(column);
  };

  return (
    <>
      {/* ================= Desktop Table ================= */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-green-900 font-semibold">
            <tr>
              <th className="px-4 py-3 text-left">
                <button onClick={() => handleSort("code")}>
                  {t("coupon_code")}
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button onClick={() => handleSort("type")}>
                  {t("type")}
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button onClick={() => handleSort("value")}>
                  {t("discount")}
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button onClick={() => handleSort("start_date")}>
                  {t("valid_period")}
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button onClick={() => handleSort("number_of_uses_for_user")}>
                  {t("usage")}
                </button>
              </th>

              <th className="px-4 py-3 text-left">
                <button onClick={() => handleSort("status")}>
                  {t("status")}
                </button>
              </th>

              <th className="px-4 py-3 text-center w-32">
                {t("actions")}
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {coupons?.map((coupon) => (
              <tr
                key={coupon.uuid}
                className="hover:bg-muted/30 transition"
                onMouseEnter={() => setHoveredRow(coupon.uuid)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td className="px-4 py-3 font-mono">
                  {coupon.code}
                </td>

                <td className="px-4 py-3">
                  {t(coupon.type)}
                </td>

                <td className="px-4 py-3">
                  <div>{getDiscountDisplay(coupon)}</div>
                  <div className="text-xs text-muted-foreground">
                    {t(coupon.condition)}
                  </div>
                </td>

                <td className="px-4 py-3">
                  {formatDate(coupon.start_date)} –{" "}
                  {formatDate(coupon.end_date)}
                </td>

                <td className="px-4 py-3">
                  {coupon.number_of_uses_for_user} /{" "}
                  {coupon.usage_limit || "∞"}
                </td>

                <td className="px-4 py-3">
                  {getStatusBadge(coupon.status)}
                </td>

                <td className="px-4 py-3">
                  <div className="flex justify-center gap-1">
                    <button onClick={() => onEdit(coupon)}>
                      <Icon name="Edit" size={14} />
                    </button>
                    <button onClick={() => onDuplicate(coupon)}>
                      <Icon name="Copy" size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(coupon)}
                      className="text-destructive"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ================= Mobile Cards ================= */}
      <div className="lg:hidden divide-y divide-border">
        {coupons?.map((coupon) => (
          <div key={coupon.uuid} className="p-4 space-y-3">
            <div className="flex justify-between">
              <div>
                <div className="font-mono font-medium">{coupon.code}</div>
                <div className="text-sm">{t(coupon.type)}</div>
              </div>
              {getStatusBadge(coupon.status)}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Discount</span>
                <div>{getDiscountDisplay(coupon)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Usage</span>
                <div>
                  {coupon.number_of_uses_for_user} /{" "}
                  {coupon.usage_limit || "∞"}
                </div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              {formatDate(coupon.start_date)} –{" "}
              {formatDate(coupon.end_date)}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleStatus(coupon)}
              >
                {coupon.status === "active" ? "Pause" : "Activate"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEdit(coupon)}>
                <Icon name="Edit" size={14} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDuplicate(coupon)}>
                <Icon name="Copy" size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(coupon)}
                className="text-destructive"
              >
                <Icon name="Trash2" size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {coupons?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Ticket" size={48} className="mx-auto mb-4 text-muted" />
          <h3 className="text-lg font-medium">{t("no_cop")}</h3>
        </div>
      )}
    </>
  );
};

export default CouponTable;
